import {useCallback, useMemo} from 'react';

import {useDispatch, useSelector} from 'react-redux';

import {
  selectAutoCompleteCityName,
  selectAutoCompleteStreetName,
} from '~/shared/store/storeModules/autoComplete/autoCompleteSelectors';
import {selectCompanyAddresses} from '~/shared/store/storeModules/companyAddresses/companyAddressesSelectors';
import {
  selectCurrentDeliveryMethod,
} from '~/shared/store/storeModules/currentDeliveryMethod/currentDeliveryMethodSelectors';
import {selectCurrentAddressKey} from '~/shared/store/storeModules/currentAddressKey/currentAddressKeySelectors';
import {
  selectAddressView,
  selectAllAddresses,
  selectEditedAddress,
  selectIsCurrentAddressIsDinningRoom,
  selectRemoteAddresses,
  selectUserData,
} from '~/shared/store/selectors';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {trackEvent} from '~/shared/services/analytics';
import {Address, LocalAddress} from '~/shared/store/models';
import {setLocalAddress as setLocalAddressAction} from '~/shared/store/storeModules/localAddress/localAddressActions';
import {
  fetchCityNames as fetchCityNamesAction,
  fetchStreetNames as fetchStreetNamesAction,
} from '~/shared/store/storeModules/autoComplete/autoCompleteActions';
import {getCompanyAddresses as getCompanyAddressesAction} from '~/shared/store/storeModules/companyAddresses';
import {setAddressView, setEditedAddress} from '~/shared/store/storeModules/ui/uiActions';
import {useIsMaxLargeMobile, useIsMaxMobile, useIsMinLargeMobile, useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';
import {getUserAddresses, removeAddress as removeAddressAction} from '~/shared/store/storeModules/user/userActions';
import {UiState} from '~/shared/store/storeModules/ui/uiReducer';
import {AddressViewValuesMap} from '~/shared/consts/addressConsts';
import {setIsNdeAnalyticsIdle} from '~/shared/store/storeModules/ndeAnalytics/ndeAnalyticsActions';
import {addressKeyFromParts, isAddressKeyContainAddressId} from '~/shared/utils/address';
import apiService from '~/shared/services/apiService';
import store from '~/shared/store';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {createLogger} from '~/shared/logging';
import {getExtraGeoCoderDataForAddress} from '~/shared/services/geocoder';

const logger = createLogger('addressDropdown');

const useAddressDropdownContextValue = ({onOpen, isMenuPage}: {
  onOpen?: () => void;
  isMenuPage: boolean;
}) => {
  const dispatch = useDispatch();

  const cityNameAutocomplete = useSelector(selectAutoCompleteCityName);
  const streetNameAutocomplete = useSelector(selectAutoCompleteStreetName);
  const companyAddresses = useSelector(selectCompanyAddresses);
  const deliveryMethod = useSelector(selectCurrentDeliveryMethod);
  const addressKey = useSelector(selectCurrentAddressKey);
  const initialAddresses = useSelector(selectAllAddresses);
  const userData = useSelector(selectUserData);
  const addressView = useSelector(selectAddressView);
  const editedAddress = useSelector(selectEditedAddress);
  const isCurrentAddressIsDinningRoom = useSelector(selectIsCurrentAddressIsDinningRoom);

  const isMaxMobile = useIsMaxMobile();
  const isMaxLargeMobile = useIsMaxLargeMobile();
  const isMinLargeMobile = useIsMinLargeMobile();
  const isMinLargeTablet = useIsMinLargeTablet();

  const setLocalAddress = useCallback((localAddress: LocalAddress) => dispatch(setLocalAddressAction(localAddress)), [dispatch]);
  const fetchCityNames = useCallback((cityName: string) => dispatch(fetchCityNamesAction(cityName)), [dispatch]);
  const fetchStreetNames = useCallback((args: {searchValue: string; parentId: string}) => dispatch(fetchStreetNamesAction(args)), [dispatch]);
  const getCompanyAddresses = useCallback((location: Partial<Address>) => dispatch(getCompanyAddressesAction(location)), [dispatch]);
  const onSubmitEditedAddress = useCallback((newEditedAddress: Address) => dispatch(setEditedAddress(newEditedAddress)), [dispatch]);
  const getAddresses = useCallback((restId = null) => dispatch(getUserAddresses(restId)), [dispatch]);
  const onViewChange = useCallback((view: UiState['addressDropdown']['addressView']) => dispatch(setAddressView(view)), [dispatch]);

  const onDeliveryMethodChange = useCallback((changedDeliveryMethod: DeliveryMethods) => {
    ManagerProvider.changeDeliveryMethod({deliveryMethod: changedDeliveryMethod, searchRestaurants: !isMenuPage});
    trackEvent('hasSwitchedDeliveryType', {deliveryMethod: changedDeliveryMethod});
  }, [isMenuPage]);

  const onClose = useCallback((e?: Event) => {
    if (addressView === AddressViewValuesMap.EDIT || addressView === AddressViewValuesMap.EDIT_LOCAL) {
      onViewChange(AddressViewValuesMap.MAIN);
      if (e) {
        e.stopPropagation();
        return;
      }
    }

    onViewChange(null);
  }, [onViewChange, addressView]);

  const handleSubmitLocalAddress = useCallback((submittedLocalAddress: LocalAddress) => {
    dispatch(setLocalAddressAction(submittedLocalAddress));
    dispatch(setIsNdeAnalyticsIdle(false));
    trackEvent('hasModifiedAddress', {modifiedAddressType: 'add address', modifiedLocation: 'address dropdown'});
    onClose();
  }, [dispatch, onClose]);

  const changeAddress = useCallback(async ({address}: {address: Address}) => {
    onClose();
    await ManagerProvider.changeAddress({address, searchRestaurants: !isMenuPage});

    const isAddressId = isAddressKeyContainAddressId(address.addressKey);

    if (isAddressId) {
      const addressType = address.isCompanyAddress ? 'office' : 'private';
      trackEvent('hasChangedLocation', {addressType});
    } else {
      trackEvent('hasChangedLocation', {addressType: 'locationBased'});
    }
  }, [onClose, isMenuPage]);

  const handleAddCompanyAddress = useCallback(async (companyAddressId: number) => {
    try {
      const {data: newAddressId} = await apiService.appendCompanyAddressToUser({companyAddressId});
      await getAddresses();

      const state = store.getState();
      const updatedAddressesAfterFetch = selectRemoteAddresses(state);

      const newAddressObjectFromServer = updatedAddressesAfterFetch.find(
        ({addressId}) => `${addressId}` === `${newAddressId}`,
      );

      if (newAddressObjectFromServer) {
        return changeAddress({address: newAddressObjectFromServer});
      }
    } catch (error) {
      if (is401Error(error)) {
        await handleRefreshToken(error, handleAddCompanyAddress);
        return;
      }
      logger.error('handleAddCompanyAddress error', error);
    }
  }, [changeAddress, getAddresses]);

  const onSubmit = useCallback(async (values: Address & {phoneNumber: string}) => {
    const geocoderResult = await getExtraGeoCoderDataForAddress(values);

    const {latitude, longitude} = geocoderResult || {};

    if (!latitude || !longitude) {
      return;
    }

    const newAddressWithUpdatedAddressKey = {
      ...values,
      streetId: Number(values.streetId),
      cityId: Number(values.cityId),
      latitude,
      longitude,
      addressKey: addressKeyFromParts(values),
      phone01: values.phoneNumber || values.phone01,
    };

    if (values.addressId) {
      await ManagerProvider.updateAddress(newAddressWithUpdatedAddressKey);
      onViewChange(AddressViewValuesMap.MAIN);
      trackEvent('hasModifiedAddress', {modifiedAddressType: 'edit address', modifiedLocation: 'address dropdown'});
    }
  }, [onViewChange]);

  const deleteAddress = useCallback(async (addresses: Address[]) => {
    try {
      const {cityId, houseNumber, streetId, addressId} = editedAddress;

      const availableAddresses = addresses.filter(
        address => address.cityId !== cityId || address.houseNumber !== houseNumber || address.streetId !== streetId,
      );

      if (addresses.length > 1) {
        if (addressId) {
          await dispatch(removeAddressAction(addressId));
          trackEvent('hasModifiedAddress', {modifiedAddressType: 'remove address', modifiedLocation: 'address dropdown'});
        }

        onViewChange(AddressViewValuesMap.MAIN);
        changeAddress({address: availableAddresses[0]});
      }
    } catch (error) {
      if (is401Error(error)) {
        handleRefreshToken(error, deleteAddress, addresses);
      }
    }
  }, [changeAddress, dispatch, editedAddress, onViewChange]);

  const toggleDropdown = useCallback(() => {
    if (addressView) {
      onViewChange(null);
    } else {
      onOpen?.();
      onViewChange(AddressViewValuesMap.MAIN);
      trackEvent('hasClickedAddressDropdown');
    }
  }, [addressView, onOpen, onViewChange]);

  return useMemo(() => ({
    cityNameAutocomplete,
    streetNameAutocomplete,
    companyAddresses,
    deliveryMethod,
    addressKey,
    initialAddresses,
    userData,
    addressView,
    editedAddress,
    isMenuPage,
    isMaxMobile,
    isMaxLargeMobile,
    isMinLargeMobile,
    isMinLargeTablet,
    onDeliveryMethodChange,
    isCurrentAddressIsDinningRoom,
    setLocalAddress,
    fetchCityNames,
    fetchStreetNames,
    getCompanyAddresses,
    onSubmitEditedAddress,
    onViewChange,
    onClose,
    handleSubmitLocalAddress,
    changeAddress,
    onSubmit,
    deleteAddress,
    toggleDropdown,
    handleAddCompanyAddress,
  }), [
    cityNameAutocomplete,
    streetNameAutocomplete,
    companyAddresses,
    deliveryMethod,
    addressKey,
    initialAddresses,
    userData,
    addressView,
    editedAddress,
    isMenuPage,
    isMaxMobile,
    isMaxLargeMobile,
    isMinLargeMobile,
    isMinLargeTablet,
    onDeliveryMethodChange,
    isCurrentAddressIsDinningRoom,
    setLocalAddress,
    fetchCityNames,
    fetchStreetNames,
    getCompanyAddresses,
    onSubmitEditedAddress,
    onViewChange,
    onClose,
    handleSubmitLocalAddress,
    changeAddress,
    onSubmit,
    deleteAddress,
    toggleDropdown,
    handleAddCompanyAddress,
  ]);
};

export default useAddressDropdownContextValue;
